<template>
  <div class="summernote-iframe-wrapper">
    <iframe
      ref="editorIframe"
      class="w-100 border-0 d-block"
      :style="{ height: computedHeight, backgroundColor: '#ffffff' }"
      sandbox="allow-same-origin allow-scripts"
    ></iframe>
  </div>
</template>

<script>
import $ from 'jquery'
import 'summernote/dist/summernote-lite.min'
import summernoteCss from './summernoteCss.js'

export default {
  name: 'CmpEditorIframe',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    height: {
      type: [String, Number],
      default: '650px'
    },
    movil: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'input', 'change'],
  data() {
    return {
      contenidoInterno: '',
      inicializado: false
    }
  },
  computed: {
    computedHeight() {
      if (typeof this.height === 'number') {
        return `${this.height}px`
      }
      return this.height.includes('px') ? this.height : `${this.height}px`
    }
  },
  watch: {
    modelValue(nuevoValor) {
      if (this.inicializado && nuevoValor !== this.contenidoInterno) {
        this.contenidoInterno = nuevoValor || ''
        this.actualizarCodigoSummernote(this.contenidoInterno)
      }
    },
    value(nuevoValor) {
      if (this.inicializado && nuevoValor !== this.contenidoInterno) {
        this.contenidoInterno = nuevoValor || ''
        this.actualizarCodigoSummernote(this.contenidoInterno)
      }
    }
  },
  mounted() {
    this.contenidoInterno = this.modelValue || this.value || ''
    this.$nextTick(() => {
      this.iniciarEditorIframe()
    })
  },
  methods: {
    iniciarEditorIframe() {
      const iframe = this.$refs.editorIframe
      if (!iframe) return

      const doc = iframe.contentDocument || iframe.contentWindow?.document
      if (!doc) return

      // Estructura HTML que se renderiza dentro del iframe
      const htmlBase = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            /* Inyección aislada de Summernote Lite CSS */
            ${summernoteCss}

            /* Estilos base del iframe */
            html, body {
              margin: 0;
              padding: 4px;
              background-color: #ffffff;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              font-size: 14px;
              box-sizing: border-box;
            }

            .note-editor.note-frame {
              border: 1px solid #ced4da;
              border-radius: 4px;
              margin-bottom: 0;
            }

            .note-editable {
              background-color: #ffffff !important;
            }

            /* Clases utilitarias de Bootstrap para soporte en el diseño de facturas */
            .row {
              display: flex;
              flex-wrap: wrap;
              margin-right: -10px;
              margin-left: -10px;
            }
            .col-md-1, .col-md-2, .col-md-3, .col-md-4, .col-md-5, .col-md-6,
            .col-md-7, .col-md-8, .col-md-9, .col-md-10, .col-md-11, .col-md-12 {
              box-sizing: border-box;
              padding-right: 10px;
              padding-left: 10px;
            }
            .col-md-12 { flex: 0 0 100%; max-width: 100%; }
            .col-md-6  { flex: 0 0 50%; max-width: 50%; }
            .col-md-5  { flex: 0 0 41.666667%; max-width: 41.666667%; }
            .col-md-7  { flex: 0 0 58.333333%; max-width: 58.333333%; }
            .col-md-4  { flex: 0 0 33.333333%; max-width: 33.333333%; }
            .col-md-8  { flex: 0 0 66.666667%; max-width: 66.666667%; }
            .text-right  { text-align: right; }
            .text-left   { text-align: left; }
            .text-center { text-align: center; }
            .text-justify{ text-align: justify; }
          </style>
        </head>
        <body>
          <div id="summernoteArea"></div>
        </body>
        </html>
      `

      doc.open()
      doc.write(htmlBase)
      doc.close()

      // Exponer jQuery en el window del iframe
      iframe.contentWindow.jQuery = $
      iframe.contentWindow.$ = $

      let toolbar = [
        ['style', ['style', 'bold', 'italic', 'underline', 'clear']],
        ['font', ['strikethrough', 'superscript', 'subscript']],
        ['fontsize', ['fontsize']],
        ['color', ['color']],
        ['para', ['ul', 'ol', 'paragraph']],
        ['table', ['table']],
        ['height', ['height']],
        ['view', ['codeview', 'help']]
      ]

      if (this.movil) {
        toolbar = [
          ['style', ['style', 'bold', 'italic', 'underline']],
          ['fontsize', ['fontsize', 'color']],
          ['para', ['ul', 'paragraph']],
          ['table', ['table', 'height']],
          ['view', ['codeview']]
        ]
      }

      const self = this
      const alturaEditor = Math.max(parseInt(this.height) - 100, 480)

      const $editor = $(doc.getElementById('summernoteArea'))
      $editor.summernote({
        height: alturaEditor,
        toolbar: toolbar,
        callbacks: {
          onChange: function(contents) {
            self.contenidoInterno = contents
            self.$emit('update:modelValue', contents)
            self.$emit('input', contents)
            self.$emit('change', contents)
          }
        }
      })

      // Asignar el contenido inicial
      if (this.contenidoInterno) {
        $editor.summernote('code', this.contenidoInterno)
      }

      this.inicializado = true
    },

    actualizarCodigoSummernote(html) {
      const iframe = this.$refs.editorIframe
      if (!iframe) return

      const doc = iframe.contentDocument || iframe.contentWindow?.document
      if (!doc) return

      const $editor = $(doc.getElementById('summernoteArea'))
      if ($editor.length && typeof $editor.summernote === 'function') {
        const codigoActual = $editor.summernote('code')
        if (codigoActual !== html) {
          $editor.summernote('code', html || '')
        }
      }
    },

    /**
     * Método público para asignar código HTML directamente
     */
    setCode(html) {
      this.contenidoInterno = html || ''
      this.actualizarCodigoSummernote(this.contenidoInterno)
      this.$emit('update:modelValue', this.contenidoInterno)
      this.$emit('input', this.contenidoInterno)
      this.$emit('change', this.contenidoInterno)
    },

    /**
     * Método público para obtener el código HTML actual
     */
    getCode() {
      const iframe = this.$refs.editorIframe
      if (!iframe) return this.contenidoInterno

      const doc = iframe.contentDocument || iframe.contentWindow?.document
      if (!doc) return this.contenidoInterno

      const $editor = $(doc.getElementById('summernoteArea'))
      if ($editor.length && typeof $editor.summernote === 'function') {
        return $editor.summernote('code')
      }
      return this.contenidoInterno
    }
  }
}
</script>

<style scoped>
.summernote-iframe-wrapper {
  width: 100%;
  position: relative;
  background-color: #ffffff;
  border-radius: 4px;
}
</style>
